import React from "react";
import { Trash, Edit } from "lucide-react";
import { EDSButton } from "../../../../eds/EDSButton";
import { DietaryRequirement } from "../../../../../types/bookings";
import { isValidNoteContent } from "../../../../../utils/booking";
import { displayConfirmDialog } from "../../../../../utils/confirmDialog";

type DietaryRequirementsSectionProps = {
  dietaryRequirements: DietaryRequirement[] | undefined;
  guestCardClasses: (name?: string) => string;
  formatDietaryRequirements: (requirement: DietaryRequirement) => string;
  handleEditDietaryRequirement: (
    requirement: DietaryRequirement,
    index: number
  ) => void;
  handleDeleteDietaryRequirement: (index: number) => Promise<void>;
  handleAddDietaryRequirement: () => void;
};

const DietaryRequirementsSection = ({
  dietaryRequirements,
  guestCardClasses,
  formatDietaryRequirements,
  handleEditDietaryRequirement,
  handleDeleteDietaryRequirement,
  handleAddDietaryRequirement
}: DietaryRequirementsSectionProps): React.ReactElement => {
  return (
    <section className="py-4 px-4">
      <h3 className="text-sm font-semibold mb-3">Dietary Requirements</h3>

      {dietaryRequirements && dietaryRequirements.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {dietaryRequirements
            .map((r) => ({
              ...r,
              diet_types: Array.isArray(r.diet_types) ? r.diet_types : [],
              allergies: Array.isArray(r.allergies) ? r.allergies : []
            }))
            .map((req: DietaryRequirement, index: number) => {
              const colorClasses = guestCardClasses(req.name);
              return (
                <div
                  key={`${req.name ?? "req"}-${(req.diet_types ?? []).join(",")}-${(req.allergies ?? []).join(",")}`}
                  className={`border rounded-lg p-3 ${colorClasses ?? "bg-muted border-border"}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-semibold text-base">{req.name}</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {formatDietaryRequirements(req)}
                      </div>
                      {isValidNoteContent(req.notes) && (
                        <div className="text-sm text-muted-foreground mt-2">
                          {req.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <EDSButton
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditDietaryRequirement(req, index)}
                        aria-label={`Edit dietary requirements for ${req.name}`}
                      >
                        <Edit className="h-4 w-4" />
                      </EDSButton>
                      <EDSButton
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          const confirmDelete = await displayConfirmDialog({
                            title: "Delete Dietary Requirement?",
                            message: `Are you sure you wish to delete dietary requirements for ${req.name}?`,
                            accept: "Confirm",
                            cancel: "Cancel"
                          });
                          if (confirmDelete) {
                            handleDeleteDietaryRequirement(index);
                          }
                        }}
                        aria-label={`Delete dietary requirements for ${req.name}`}
                      >
                        <Trash className="h-4 w-4" />
                      </EDSButton>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-4">
          No dietary requirements added yet.
        </div>
      )}

      <div className="mt-4">
        <EDSButton
          variant="default"
          size="sm"
          onClick={handleAddDietaryRequirement}
        >
          + Add Dietary Requirement
        </EDSButton>
      </div>
    </section>
  );
};

export default DietaryRequirementsSection;
